/* ==========================================================================
   Pacem collection infinite scroll

   Wraps the collection grid's pagination. When the shopper nears the bottom
   of the grid, the next page is fetched through the Section Rendering API and
   its cards are appended to #product-grid. The new cards keep Dawn's
   scroll-trigger classes and are observed with the same handler as
   animations.js, so each row fades in exactly like the first page's rows.

   Without JS (or if a fetch fails) the ordinary pagination links stay usable.
   The element lives inside #ProductGridContainer, so when facets.js
   re-renders the grid for a new sort order, a fresh element starts over from
   page 2 of that order.
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('pacem-infinite-scroll')) return;

  class PacemInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.nextUrl = this.dataset.nextUrl;
      this.sectionId = this.dataset.sectionId;
      this.grid = document.getElementById('product-grid');
      this.spinner = this.querySelector('.loading__spinner');
      this.loading = false;
      if (!this.nextUrl || !this.grid || !('IntersectionObserver' in window)) return;

      this.classList.add('is-active');
      // A scroll check rather than an IntersectionObserver: the sentinel is 0px
      // tall, and jumping past it (End key, a fast fling) is not a threshold
      // crossing, so an observer would never fire.
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.check();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.check();
    }

    disconnectedCallback() {
      this.stop();
    }

    stop() {
      if (!this.onScroll) return;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.onScroll = null;
    }

    check() {
      if (this.getBoundingClientRect().top < window.innerHeight + 800) this.loadNext();
    }

    async loadNext() {
      if (this.loading || !this.nextUrl) return;
      this.loading = true;
      if (this.spinner) this.spinner.classList.remove('hidden');

      try {
        const url = new URL(this.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(response.status);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');

        const items = Array.from(html.querySelectorAll('#product-grid > .grid__item'));
        items.forEach((item) => this.grid.appendChild(item));
        this.reveal(items);

        const next = html.querySelector('pacem-infinite-scroll');
        this.nextUrl = next ? next.dataset.nextUrl : '';
      } catch (error) {
        // Leave the pagination links in place as the fallback.
        this.nextUrl = '';
        this.classList.remove('is-active');
      }

      if (this.spinner) this.spinner.classList.add('hidden');
      this.loading = false;

      if (!this.nextUrl) {
        this.stop();
        return;
      }

      // If the new page didn't fill the screen, load the next one too.
      this.check();
    }

    /* Same observer set-up as initializeScrollAnimationTrigger in animations.js,
       limited to the new cards so rows already shown don't re-animate. */
    reveal(items) {
      if (typeof onIntersection !== 'function') return;
      const triggers = items.filter((item) => item.classList.contains('scroll-trigger'));
      if (!triggers.length) return;
      const observer = new IntersectionObserver(onIntersection, { rootMargin: '0px 0px -50px 0px' });
      triggers.forEach((item) => observer.observe(item));
    }
  }

  customElements.define('pacem-infinite-scroll', PacemInfiniteScroll);
})();
